import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payments';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WebGrid from '../../webgrid/src';
import ContractPaymentDetails from '../components/ContractPaymentDetails';
import PageHeaderBanner from '../components/PageHeaderBanner';
import {
    BatchOutcome,
    BatchPaidContract,
    getAmountForSettings,
    getBatchOutcomeSeverity,
    getPanelContracts,
    getPaymentSettings,
    getSelectedContracts,
    summarizeBatchPayment,
} from '../domain/batchPayment';
import {
    ContractData,
    ContractPaymentSettings,
    PaymentCalculation,
} from '../domain/paymentContracts';
import { getContractAmountColumns, getContractDateColumns } from '../domain/contractColumns';
import {
    buildPaymentCalculationPath,
    calculationRequestKey,
} from '../domain/contractBonusOverride';
import useRequest from '../hooks/useRequest';
import useCashBookingReadiness from '../hooks/useCashBookingReadiness';
import {
    CashBookingRefusalBanner,
    CashBookingRefusalDialog,
} from '../components/CashBookingRefusal';
import { useSumatorPageCollapse } from '../hooks/useSumatorPageCollapse';
import { useDeferredQuery } from '../hooks/useQuery';
import { useAuth } from '../contexts/AuthContext';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import { useSumator } from '../contexts/SumatorContext';

interface AllContractsProjection {
    mode: 'all';
    contractCount: number;
    totalAmount: number;
    targetDate: string;
    daysUntil: number;
}

interface SingleContractProjection {
    mode: 'single';
    contractNumber: string;
    baseAmount: number;
    currentCommission: number;
    simulatedCommission: number;
    discount: number;
    discountPercent: number;
    includesBaseAmount: boolean;
    totalAmount: number;
    targetDate: string;
    daysUntil: number;
}

function calculateAllContractsProjection(
    allCalculations: Map<number, PaymentCalculation>,
    payableContracts: ContractData[],
    targetDate: dayjs.Dayjs,
    daysUntilTarget: number,
    simulationIncludeBaseAmount: boolean,
    simulationWithDiscount: boolean,
): AllContractsProjection {
    let totalProjectedAmount = 0;
    let contractCount = 0;

    for (const contract of payableContracts) {
        const calc = allCalculations.get(contract.Nr);
        if (!calc) continue;

        const simulatedCommission = daysUntilTarget * calc.commissionPerDay;
        // Days a promotion waived stay waived; the simulated days are charged in full.
        const totalCommission =
            calc.commission + simulatedCommission - (calc.promotion?.amount ?? 0);
        const discountAmount = simulationWithDiscount ? calc.discount : 0;

        const amount = simulationIncludeBaseAmount
            ? calc.baseAmount + totalCommission - discountAmount
            : totalCommission - discountAmount;

        totalProjectedAmount += amount;
        contractCount++;
    }

    return {
        mode: 'all' as const,
        contractCount,
        totalAmount: totalProjectedAmount,
        targetDate: targetDate.format('YYYY-MM-DD'),
        daysUntil: daysUntilTarget,
    };
}

function calculateSingleContractProjection(
    selectedContract: ContractData,
    calculation: PaymentCalculation,
    daysUntilTarget: number,
    targetDate: dayjs.Dayjs,
    simulationWithDiscount: boolean,
    simulationIncludeBaseAmount: boolean,
): SingleContractProjection {
    const additionalDays = daysUntilTarget;
    const simulatedCommission = additionalDays * calculation.commissionPerDay;
    const discountAmount = simulationWithDiscount ? calculation.discount : 0;
    // Days a promotion waived stay waived; the simulated days are charged in full.
    const totalCommission =
        calculation.commission + simulatedCommission - (calculation.promotion?.amount ?? 0);
    const projectedTotal = simulationIncludeBaseAmount
        ? calculation.baseAmount + totalCommission - discountAmount
        : totalCommission - discountAmount;

    return {
        mode: 'single' as const,
        contractNumber: selectedContract.NrContract,
        baseAmount: calculation.baseAmount,
        currentCommission: calculation.commission,
        simulatedCommission,
        discount: discountAmount,
        discountPercent: calculation.discountPercent,
        includesBaseAmount: simulationIncludeBaseAmount,
        totalAmount: projectedTotal,
        targetDate: targetDate.format('YYYY-MM-DD'),
        daysUntil: daysUntilTarget,
    };
}

interface ExtensionHistoryItem {
    extensionNumber: number;
    extensionDate: string | null;
    amountRon: string | null;
    amountEur: string | null;
    newDueDate: string | null;
    originalDueDate: string | null;
    extensionDays: number | null;
}

interface ClientData {
    ClNr: number;
    Nume: string;
    CNP?: string;
    Oras?: string;
    Str?: string;
    Nr?: string;
    Telefon?: string;
    photoUrl?: string;
    [key: string]: unknown;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
}

const Payment = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const request = useRequest();
    const { addLine } = useSumator();
    const [searchParams] = useSearchParams();
    const { API_BASE_URL, token, user } = useAuth();
    // Only an administrator may set a per-payment contract discount (SuperBonusC, BL-04).
    const isAdmin = user?.role === 'ADMIN';
    // The workstation's register: the server books the payment into the session open there now
    // (a session number kept since login was stale after a handover).
    const { refreshBalance, cashPointId } = useCashDrawer();
    // A payment is booked into the drawer: a station that cannot book it is told so on opening
    // the page, not after the operator has set the payment up.
    const { refusalKey: bookingRefusalKey, ensureCanBook } = useCashBookingReadiness({
        checkOnMount: true,
    });
    const [bookingRefusalDialogKey, setBookingRefusalDialogKey] = useState<string | null>(null);

    // Read from the URL during render, not copied into state by an effect: the queries
    // below would otherwise run once with no client, settle as "loaded, nothing found",
    // and show "No payable contracts" until the real fetch answered.
    const clientId = useMemo(() => {
        const id = Number.parseInt(searchParams.get('clientId') ?? '', 10);
        return Number.isNaN(id) ? null : id;
    }, [searchParams]);
    // The row-clicked contract is held as an id rather than a row object, for the same
    // reason the batch selection is: it has to resolve against the freshest fetch. A row
    // object captured on click keeps rendering its own stale copy after the contract has
    // been paid off and left the payable list.
    const [inspectedContractNr, setInspectedContractNr] = useState<number | null>(null);
    // One entry per contract that has a panel on screen: a selection may mix a
    // redemption with a prolongation, so payment type, days and discount are held per
    // contract rather than per page.
    const [paymentSettings, setPaymentSettings] = useState<Map<number, ContractPaymentSettings>>(
        new Map(),
    );
    const [calculations, setCalculations] = useState<Map<number, PaymentCalculation>>(new Map());
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [confirmContract, setConfirmContract] = useState<ContractData | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);

    // Extension history modal state
    const [extensionHistoryOpen, setExtensionHistoryOpen] = useState<boolean>(false);
    const [extensionHistory, setExtensionHistory] = useState<ExtensionHistoryItem[]>([]);
    const [extensionHistoryLoading, setExtensionHistoryLoading] = useState<boolean>(false);

    // Simulation state
    const [simulationDate, setSimulationDate] = useState<Dayjs | null>(dayjs().add(30, 'day'));
    const [simulateAllContracts, setSimulateAllContracts] = useState<boolean>(false);
    const [simulationWithDiscount, setSimulationWithDiscount] = useState<boolean>(true);
    const [simulationIncludeBaseAmount, setSimulationIncludeBaseAmount] = useState<boolean>(true);
    const [allCalculations, setAllCalculations] = useState<Map<number, PaymentCalculation>>(
        new Map(),
    );

    // Batch "pay and print" state. `batchSelection` holds contract Nr values in the
    // order they were ticked, which is the order they are settled and printed in.
    const [batchSelection, setBatchSelection] = useState<number[]>([]);
    const [batchDialogOpen, setBatchDialogOpen] = useState<boolean>(false);
    const [batchProcessing, setBatchProcessing] = useState<boolean>(false);
    const [batchProgress, setBatchProgress] = useState<number>(0);
    const [batchOutcome, setBatchOutcome] = useState<BatchOutcome | null>(null);
    // Bumped after a batch run so the panels that survive it (a prolonged contract stays
    // payable) re-price against the database instead of keeping the figures they were
    // charged under. The set of panel contracts alone cannot detect this — it may be
    // unchanged across the payment.
    const [calculationsToken, setCalculationsToken] = useState<number>(0);

    // Keep the Sumator out of the way while the operator reviews payment details.
    useSumatorPageCollapse();

    // Fetch client details
    const { data: clientData } = useDeferredQuery<{ client: ClientData }>({
        path: clientId ? `/clients/${clientId}` : '/clients/0',
        method: 'GET',
        shouldExecute: !!clientId,
    });

    const client = clientData?.client ?? null;

    // Fetch contracts for the client
    const {
        data: contractsData,
        loading: contractsLoading,
        reload: reloadContracts,
    } = useDeferredQuery<{
        contracts: ContractData[];
        pagination: Pagination;
    }>({
        path: clientId ? `/contracts?clnr=${clientId}&limit=10000` : '/contracts?limit=0',
        method: 'GET',
        shouldExecute: !!clientId,
    });

    // Filter only payable contracts (Active, Passive, Expired - those that can be redeemed or prolonged)
    const payableContracts = useMemo(() => {
        const allContracts = contractsData?.contracts ?? [];
        return allContracts.filter(contract => {
            // Check both 'status' (new column name) and 'Statut' (legacy) for compatibility
            const status = contract.status ?? contract.Statut;
            // Active (null or ''), Passive ('!'), Expired ('!!') can be paid
            // Stock ('INV', 'EXP') and OK cannot be paid
            return status === null || status === '' || status === '!' || status === '!!';
        });
    }, [contractsData?.contracts]);

    const selectedBatchContracts = useMemo(
        () => getSelectedContracts(batchSelection, payableContracts),
        [batchSelection, payableContracts],
    );

    const panelContracts = useMemo(
        () => getPanelContracts(batchSelection, inspectedContractNr, payableContracts),
        [batchSelection, inspectedContractNr, payableContracts],
    );

    // Identity for the calculation effect: re-fetch when the set of contracts or a contract's
    // discount override changes, not when the row objects are replaced by a refetch.
    const contractBonusPercentOf = (contractNr: number) =>
        getPaymentSettings(paymentSettings, contractNr).contractBonusPercent;
    const panelCalculationKey = calculationRequestKey(panelContracts, contractBonusPercentOf);

    // Fetch calculations for all contracts when "all contracts" simulation is toggled
    useEffect(() => {
        if (!simulateAllContracts || payableContracts.length === 0) return;

        const fetchAllCalculations = async () => {
            const calcMap = new Map<number, PaymentCalculation>();
            const promises = payableContracts.map(async contract => {
                try {
                    const response = await request<{
                        success: boolean;
                        data: PaymentCalculation;
                    }>({
                        path: `/payments/calculate/${contract.Nr}`,
                        method: 'GET',
                    });
                    if (response.success && response.data) {
                        calcMap.set(contract.Nr, response.data);
                    }
                } catch {
                    // Skip contracts that fail calculation
                }
            });
            await Promise.all(promises);
            setAllCalculations(calcMap);
        };

        fetchAllCalculations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [simulateAllContracts, payableContracts]);

    // The "all contracts" projection is a page-level figure; the per-contract one is
    // computed per panel below, since each panel simulates its own contract.
    const allContractsProjection = useMemo((): AllContractsProjection | null => {
        if (!simulationDate || !simulateAllContracts || allCalculations.size === 0) {
            return null;
        }

        const today = dayjs().startOf('day');
        const targetDate = simulationDate.startOf('day');
        const daysUntilTarget = targetDate.diff(today, 'day');

        if (daysUntilTarget < 0) {
            return null;
        }

        return calculateAllContractsProjection(
            allCalculations,
            payableContracts,
            targetDate,
            daysUntilTarget,
            simulationIncludeBaseAmount,
            simulationWithDiscount,
        );
    }, [
        simulationDate,
        simulateAllContracts,
        simulationWithDiscount,
        simulationIncludeBaseAmount,
        payableContracts,
        allCalculations,
    ]);

    const getProjectionForContract = (
        contract: ContractData,
        contractCalculation: PaymentCalculation | null,
    ): SingleContractProjection | null => {
        if (!simulationDate || !contractCalculation) {
            return null;
        }

        const today = dayjs().startOf('day');
        const targetDate = simulationDate.startOf('day');
        const daysUntilTarget = targetDate.diff(today, 'day');

        if (daysUntilTarget < 0) {
            return null;
        }

        return calculateSingleContractProjection(
            contract,
            contractCalculation,
            daysUntilTarget,
            targetDate,
            simulationWithDiscount,
            simulationIncludeBaseAmount,
        );
    };

    // Load a payment calculation for every contract that has a panel on screen.
    // Read-only: /payments/calculate never charges anything.
    useEffect(() => {
        if (panelContracts.length === 0) {
            setCalculations(new Map());
            return;
        }

        let cancelled = false;

        const loadCalculations = async () => {
            setLoading(true);
            setError('');

            const loaded = new Map<number, PaymentCalculation>();
            let anyFailed = false;

            await Promise.all(
                panelContracts.map(async contract => {
                    try {
                        const response = await request<{
                            success: boolean;
                            data: PaymentCalculation;
                        }>({
                            path: buildPaymentCalculationPath(
                                contract.Nr,
                                contractBonusPercentOf(contract.Nr),
                            ),
                            method: 'GET',
                        });

                        if (response.success && response.data) {
                            loaded.set(contract.Nr, response.data);
                        } else {
                            anyFailed = true;
                        }
                    } catch (err) {
                        console.error('Payment calculation error:', err);
                        anyFailed = true;
                    }
                }),
            );

            if (cancelled) {
                return;
            }

            setCalculations(loaded);
            if (anyFailed) {
                setError(t('payment.calculationFailed'));
            }
            setLoading(false);
        };

        loadCalculations();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panelCalculationKey, calculationsToken]);

    const updatePaymentSettings = (contractNr: number, next: ContractPaymentSettings) => {
        setPaymentSettings(previous => new Map(previous).set(contractNr, next));
    };

    const loadExtensionHistory = async (contractNr: number) => {
        setExtensionHistoryLoading(true);
        try {
            const response = await request<{ success: boolean; data: ExtensionHistoryItem[] }>({
                path: `/payments/extensions/${contractNr}`,
                method: 'GET',
            });

            if (response.success && response.data) {
                setExtensionHistory(response.data);
            }
        } catch (err) {
            console.error('Extension history error:', err);
        } finally {
            setExtensionHistoryLoading(false);
        }
    };

    const handleOpenExtensionHistory = (contract: ContractData) => {
        loadExtensionHistory(contract.Nr);
        setExtensionHistoryOpen(true);
    };

    const downloadInvoicePdf = async (invoiceNumber: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/payments/invoice/${invoiceNumber}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to download invoice PDF');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Chitanta_${invoiceNumber.padStart(6, '0')}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Invoice download error:', error);
            throw error;
        }
    };

    /**
     * Opens a payment's confirmation only when this station can book the money now: asked again
     * on every click, since a handover may have closed the session after the page opened.
     */
    const openWhenCanBook = async (open: () => void) => {
        const refusalKey = await ensureCanBook();
        if (refusalKey) {
            setBookingRefusalDialogKey(refusalKey);
            return;
        }
        open();
    };

    const handleProcessPayment = async () => {
        const contract = confirmContract;

        if (!contract || !calculations.get(contract.Nr)) {
            return;
        }

        const settings = getPaymentSettings(paymentSettings, contract.Nr);

        setProcessing(true);
        setError('');

        try {
            const response = await request<{
                success: boolean;
                data: { invoiceNumber: string; amount: number; message: string };
            }>({
                path: '/payments/process',
                method: 'POST',
                params: {
                    contractNr: contract.Nr,
                    paymentType: settings.paymentType,
                    extensionDays:
                        settings.paymentType === 'extension' ? settings.extensionDays : undefined,
                    applyDiscount: settings.applyDiscount,
                    contractBonusPercent: settings.contractBonusPercent,
                    paymentMethodId: 0, // Cash
                    cashPointId: cashPointId ?? undefined,
                },
            });

            if (response.success && response.data) {
                // Refresh cash drawer balance immediately after successful payment
                refreshBalance();

                // Add to Sumator
                addLine(
                    t('sumator.paymentContract', { nr: contract.NrContract }),
                    response.data.amount ?? 0,
                );

                // Show success message
                alert(
                    `${t('payment.success')}\n${t('payment.invoiceNumber')}: ${response.data.invoiceNumber}\n${t('payment.amount')}: ${(response.data.amount ?? 0).toFixed(0)} LEI\n\n${t('payment.downloadingInvoice')}`,
                );

                // Download invoice PDF
                try {
                    await downloadInvoicePdf(response.data.invoiceNumber);
                } catch (downloadError) {
                    console.error('Failed to download invoice:', downloadError);
                    alert(t('payment.invoiceDownloadFailed'));
                }

                // Navigate back to contracts with clientId to auto-select the client
                if (clientId) {
                    navigate(`/contracts?clientId=${clientId}`);
                } else {
                    navigate('/contracts');
                }
            } else {
                setError(t('payment.processingFailed'));
            }
        } catch (err) {
            console.error('Payment processing error:', err);
            setError(err instanceof Error ? err.message : t('payment.processingFailed'));
        } finally {
            setProcessing(false);
            setConfirmContract(null);
        }
    };

    // Prices straight from the panels' own calculations and settings, so the dialog can
    // never quote a different figure from the one the operator has been looking at.
    const batchSummary = useMemo(
        () => summarizeBatchPayment(selectedBatchContracts, calculations, paymentSettings),
        [selectedBatchContracts, calculations, paymentSettings],
    );

    const handleOpenBatchDialog = () =>
        void openWhenCanBook(() => {
            setBatchOutcome(null);
            setBatchDialogOpen(true);
        });

    const confirmCalculation = confirmContract ? calculations.get(confirmContract.Nr) : undefined;
    const confirmSettings = getPaymentSettings(paymentSettings, confirmContract?.Nr ?? -1);

    /**
     * Settle each selected contract and download its bill.
     *
     * Sequential on purpose: every payment writes to the same cash session and drawer
     * balance, and the operator needs the bills in a predictable order. A *payment*
     * failure stops the run and leaves the remaining contracts untouched — the ones
     * already paid stay paid, because /payments/process is per-contract and there is
     * no batch rollback. A *print* failure does not stop the run: the money has already
     * moved and the invoice number is recorded, so the bill stays reprintable.
     *
     * Each contract is settled under its own panel's settings, so one run may redeem
     * some contracts and prolong others.
     */
    const handleRunBatchPayment = async () => {
        setBatchProcessing(true);
        setBatchProgress(0);
        setError('');

        const paid: BatchPaidContract[] = [];
        const paidContractIds: number[] = [];
        let failedContractNumber: string | null = null;
        let failureReason: string | null = null;
        let stoppedAt = batchSummary.lines.length;

        for (const [index, line] of batchSummary.lines.entries()) {
            const contract = line.contract;
            const settings = line.settings;

            try {
                const response = await request<{
                    success: boolean;
                    data: { invoiceNumber: string; amount: number; message: string };
                }>({
                    path: '/payments/process',
                    method: 'POST',
                    params: {
                        contractNr: contract.Nr,
                        paymentType: settings.paymentType,
                        extensionDays:
                            settings.paymentType === 'extension'
                                ? settings.extensionDays
                                : undefined,
                        applyDiscount: settings.applyDiscount,
                        contractBonusPercent: settings.contractBonusPercent,
                        paymentMethodId: 0, // Cash
                        cashPointId: cashPointId ?? undefined,
                    },
                });

                if (!response.success || !response.data) {
                    failedContractNumber = contract.NrContract;
                    failureReason = t('payment.processingFailed');
                    stoppedAt = index;
                    break;
                }

                const amount = response.data.amount ?? 0;
                addLine(t('sumator.paymentContract', { nr: contract.NrContract }), amount);

                let printFailed = false;
                try {
                    await downloadInvoicePdf(response.data.invoiceNumber);
                } catch (downloadError) {
                    console.error('Failed to download invoice:', downloadError);
                    printFailed = true;
                }

                paid.push({
                    contractNumber: contract.NrContract,
                    invoiceNumber: response.data.invoiceNumber,
                    amount,
                    printFailed,
                });
                paidContractIds.push(contract.Nr);
                setBatchProgress(paid.length);
            } catch (err) {
                console.error('Batch payment error:', err);
                failedContractNumber = contract.NrContract;
                failureReason = err instanceof Error ? err.message : t('payment.processingFailed');
                stoppedAt = index;
                break;
            }
        }

        setBatchOutcome({
            paid,
            failedContractNumber,
            failureReason,
            notAttempted: batchSummary.lines
                .slice(stoppedAt + (failedContractNumber === null ? 0 : 1))
                .map(line => line.contract.NrContract),
        });

        // A settled contract leaves the payable list, so untick it and refetch rather
        // than trusting rows that no longer reflect the database. The refetch is what
        // retires its panel too — every panel resolves through `payableContracts`.
        setBatchSelection(previous => previous.filter(nr => !paidContractIds.includes(nr)));

        // Close the row-click panel as well, so the run ends with nothing open.
        //
        // Unticking the settled contracts empties the batch, and an empty batch makes
        // `panelContracts` fall back to whatever row was last clicked — so finishing a
        // payment used to *reveal* a payment form for some unrelated contract the
        // operator had merely inspected earlier. A form headed "Full payment details"
        // appearing at the moment a run completes reads as "this one is still pending",
        // which is exactly the wrong thing to say after money has moved. The result
        // banner is the only thing that should be left explaining what happened.
        setInspectedContractNr(null);

        // Drop what was priced before the money moved. A prolonged contract is still
        // payable and keeps its panel, so leaving its old calculation in place would show
        // the amount it was just charged as though it were still owed.
        setCalculations(previous => {
            const next = new Map(previous);
            for (const nr of paidContractIds) {
                next.delete(nr);
            }
            return next;
        });
        setCalculationsToken(previous => previous + 1);

        refreshBalance();
        reloadContracts();

        setBatchProcessing(false);
        setBatchDialogOpen(false);
    };

    const formatCurrency = (value: number | null | undefined) => {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return '0 LEI';
        }
        return `${value.toFixed(0)} LEI`;
    };

    const formatCommissionPerDay = (value: number | null | undefined) => {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return '0.00 LEI';
        }
        return `${value.toFixed(2)} LEI`;
    };

    // Helper function to map database status to display values
    const mapDatabaseStatusToDisplay = (dbStatus: string | null) => {
        if (!dbStatus || dbStatus === '') return 'ACTIV';
        switch (dbStatus) {
            case '!':
                return 'PASIV';
            case '!!':
                return 'EXPIRAT';
            case 'INV':
                return 'INV';
            case 'EXP':
                return 'EXP';
            case 'OK':
                return 'OK';
            default:
                return dbStatus;
        }
    };

    const getStatusColor = (
        status: string,
    ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
        switch (status) {
            case 'ACTIV':
                return 'success';
            case 'PASIV':
                return 'warning';
            case 'EXPIRAT':
                return 'warning';
            case 'INV':
                return 'error';
            case 'OK':
                return 'success';
            case 'EXP':
                return 'error';
            default:
                return 'default';
        }
    };

    // Contract grid columns
    const contractColumns = [
        {
            key: 'Statut',
            title: t('contracts.status'),
            dataIndex: 'Statut',
            sortable: true,
            filterable: true,
            width: 80,
            align: 'left' as const,
            headerAlign: 'left' as const,
            render: (status: string) => {
                const displayStatus = mapDatabaseStatusToDisplay(status);
                return (
                    <Chip
                        label={displayStatus}
                        color={getStatusColor(displayStatus)}
                        size="small"
                    />
                );
            },
        },
        {
            key: 'NrContract',
            title: t('contracts.number'),
            dataIndex: 'NrContract',
            sortable: true,
            filterable: true,
            width: 110,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'obiect_amanetat',
            title: t('contracts.item'),
            dataIndex: 'obiect_amanetat',
            sortable: false,
            filterable: true,
            width: 150,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        ...getContractAmountColumns(t, { amountWidth: 100, amountAlign: 'right' }),
        ...getContractDateColumns(t),
    ];

    // If no clientId provided, show error
    if (!clientId) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    {t('payment.noClientSelected')}
                    <Button variant="text" onClick={() => navigate('/contracts')} sx={{ ml: 2 }}>
                        {t('payment.goToContracts')}
                    </Button>
                </Alert>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 3 }}>
                {/* Page Title */}
                <Stack direction="row" alignItems="stretch" spacing={0} sx={{ mb: 2 }}>
                    <IconButton
                        aria-label="back"
                        onClick={() =>
                            navigate(
                                clientId ? `/contracts?clientId=${clientId}` : '/contracts',
                                clientId ? { state: { clientId } } : undefined,
                            )
                        }
                        sx={{
                            borderRadius: 0,
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            px: 1.5,
                            '&:hover': { backgroundColor: '#1565c0' },
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ flex: 1 }}>
                        <PageHeaderBanner
                            icon={<PaymentIcon />}
                            title={t('payment.title')}
                            subtitle={t('payment.subtitle')}
                            accentColor="#c2410c"
                        />
                    </Box>
                </Stack>

                <CashBookingRefusalBanner refusalKey={bookingRefusalKey} />
                <CashBookingRefusalDialog
                    refusalKey={bookingRefusalDialogKey}
                    onClose={() => setBookingRefusalDialogKey(null)}
                />

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* Client Info Section */}
                {client && (
                    <Paper sx={{ p: 1.5, mb: 2 }}>
                        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {t('payment.clientInfo')}:
                            </Typography>
                            <Typography>
                                <strong>{t('clients.name')}:</strong> {client.Nume}
                            </Typography>
                            <Typography>
                                <strong>CNP:</strong> {client.CNP ?? '-'}
                            </Typography>
                            <Typography>
                                <strong>{t('clients.city')}:</strong> {client.Oras ?? '-'}
                            </Typography>
                            <Typography>
                                <strong>{t('clients.phone')}:</strong> {client.Telefon ?? '-'}
                            </Typography>
                        </Stack>
                    </Paper>
                )}

                {/* Payable Contracts Table */}
                <Paper sx={{ mb: 3 }}>
                    <WebGrid<ContractData>
                        data={payableContracts}
                        columns={contractColumns}
                        loading={contractsLoading}
                        pagination={true}
                        pageSize={5}
                        filterBar={false}
                        exportable={false}
                        // multiSelect keeps row clicks meaning "show this contract's
                        // calculation" — WebGrid only toggles selection from a row click
                        // in single-select mode, so the checkbox stays the only way in
                        // and out of the batch.
                        selectable={true}
                        multiSelect={true}
                        rowIdAccessor={contract => contract.Nr}
                        selectedRows={selectedBatchContracts}
                        onSelectionChange={rows =>
                            setBatchSelection(rows.map(contract => contract.Nr))
                        }
                        onRowClick={contract => setInspectedContractNr(contract.Nr)}
                        onRowDoubleClick={contract => setInspectedContractNr(contract.Nr)}
                        onExport={() => {}}
                        height="250px"
                        emptyMessage={t('payment.noPayableContracts')}
                        title={`${t('payment.payableContracts')} (${payableContracts.length})`}
                    />

                    {/* Batch pay-and-print action bar */}
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}
                    >
                        <Typography variant="body2" data-testid="batch-selection-count">
                            {t('payment.batch.selectedCount', { count: batchSelection.length })}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            data-testid="batch-pay-and-print"
                            disabled={batchSelection.length === 0 || batchProcessing}
                            onClick={handleOpenBatchDialog}
                        >
                            {t('payment.batch.payAndPrint')}
                        </Button>
                        {batchSelection.length > 0 && !batchProcessing && (
                            <Button variant="text" onClick={() => setBatchSelection([])}>
                                {t('payment.batch.clearSelection')}
                            </Button>
                        )}
                    </Stack>
                </Paper>

                {/* Batch result summary */}
                {batchOutcome && (
                    <Alert
                        severity={getBatchOutcomeSeverity(batchOutcome)}
                        sx={{ mb: 3 }}
                        onClose={() => setBatchOutcome(null)}
                        data-testid="batch-result"
                    >
                        <Typography variant="body2">
                            {t('payment.batch.paidCount', { count: batchOutcome.paid.length })}
                        </Typography>
                        {batchOutcome.paid
                            .filter(entry => entry.printFailed)
                            .map(entry => (
                                <Typography variant="body2" key={entry.invoiceNumber}>
                                    {t('payment.batch.printFailedFor', {
                                        nr: entry.contractNumber,
                                        invoice: entry.invoiceNumber,
                                    })}
                                </Typography>
                            ))}
                        {batchOutcome.failedContractNumber && (
                            <Typography variant="body2">
                                {t('payment.batch.stoppedAt', {
                                    nr: batchOutcome.failedContractNumber,
                                    reason: batchOutcome.failureReason ?? '',
                                })}
                            </Typography>
                        )}
                        {batchOutcome.notAttempted.length > 0 && (
                            <Typography variant="body2">
                                {t('payment.batch.notAttempted', {
                                    contracts: batchOutcome.notAttempted.join(', '),
                                })}
                            </Typography>
                        )}
                    </Alert>
                )}

                {/* One details panel per selected contract */}
                {panelContracts.map(contract => (
                    <ContractPaymentDetails
                        key={contract.Nr}
                        contract={contract}
                        calculation={calculations.get(contract.Nr) ?? null}
                        loading={loading}
                        settings={getPaymentSettings(paymentSettings, contract.Nr)}
                        onSettingsChange={next => updatePaymentSettings(contract.Nr, next)}
                        projection={getProjectionForContract(
                            contract,
                            calculations.get(contract.Nr) ?? null,
                        )}
                        simulationDate={simulationDate}
                        onSimulationDateChange={setSimulationDate}
                        simulationIncludeBaseAmount={simulationIncludeBaseAmount}
                        onSimulationIncludeBaseAmountChange={setSimulationIncludeBaseAmount}
                        simulationWithDiscount={simulationWithDiscount}
                        onSimulationWithDiscountChange={setSimulationWithDiscount}
                        simulateAllContracts={
                            panelContracts.length === 1
                                ? {
                                      checked: simulateAllContracts,
                                      onChange: setSimulateAllContracts,
                                  }
                                : undefined
                        }
                        onOpenExtensionHistory={() => handleOpenExtensionHistory(contract)}
                        onProcessPayment={() => void openWhenCanBook(() => setConfirmContract(contract))}
                        canOverrideDiscount={isAdmin}
                        processing={processing || batchProcessing}
                        formatCurrency={formatCurrency}
                        formatCommissionPerDay={formatCommissionPerDay}
                        t={t}
                    />
                ))}

                {/* One Cancel for the page rather than one per panel: it leaves the
                    page entirely, so repeating it under every contract would imply it
                    cancels just that contract. */}
                {panelContracts.length > 0 && (
                    <Button
                        variant="outlined"
                        sx={{ mb: 3 }}
                        onClick={() =>
                            clientId
                                ? navigate(`/contracts?clientId=${clientId}`)
                                : navigate('/contracts')
                        }
                    >
                        {t('common.cancel')}
                    </Button>
                )}

                {/* Page-level projection across every payable contract */}
                {allContractsProjection && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        {t('payment.simulation.projectionFor')} {allContractsProjection.targetDate}
                        {' — '}
                        {t('payment.simulation.contractsCount')}:{' '}
                        {allContractsProjection.contractCount}
                        {' — '}
                        <strong>{formatCurrency(allContractsProjection.totalAmount)}</strong>
                    </Alert>
                )}

                {/* Show message when nothing is ticked and no row has been clicked */}
                {panelContracts.length === 0 && payableContracts.length > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {t('payment.selectContractFromTable')}
                    </Alert>
                )}

                {/* Single-contract confirmation dialog */}
                <Dialog open={confirmContract !== null} onClose={() => setConfirmContract(null)}>
                    <DialogTitle>{t('payment.confirmPayment')}</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {confirmSettings.paymentType === 'full'
                                ? t('payment.confirmFullPayment')
                                : t('payment.confirmExtensionPayment')}
                        </Typography>
                        {confirmCalculation && (
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                {t('payment.amount')}:{' '}
                                {formatCurrency(
                                    getAmountForSettings(confirmCalculation, confirmSettings),
                                )}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmContract(null)} disabled={processing}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleProcessPayment}
                            variant="contained"
                            color="primary"
                            disabled={processing}
                        >
                            {processing ? t('common.processing') : t('common.confirm')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Batch Pay & Print Confirmation Dialog */}
                <Dialog
                    open={batchDialogOpen}
                    onClose={() => !batchProcessing && setBatchDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>{t('payment.batch.confirmTitle')}</DialogTitle>
                    <DialogContent>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {t('payment.batch.confirmWarning', {
                                count: batchSummary.lines.length,
                            })}
                        </Alert>

                        {/* Each contract is settled under its own panel's settings, so the
                            type is shown per row rather than as one shared control. */}
                        <TableContainer sx={{ mt: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('contracts.number')}</TableCell>
                                        <TableCell>{t('payment.paymentType')}</TableCell>
                                        <TableCell align="right">{t('payment.amount')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {batchSummary.lines.map(line => (
                                        <TableRow key={line.contract.Nr}>
                                            <TableCell>{line.contract.NrContract}</TableCell>
                                            <TableCell>
                                                {line.settings.paymentType === 'full'
                                                    ? t('payment.fullPayment')
                                                    : t('payment.batch.extensionOf', {
                                                          days: line.settings.extensionDays,
                                                      })}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatCurrency(line.amount)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {batchSummary.unpriced.length > 0 && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {t('payment.batch.unpriced', {
                                    contracts: batchSummary.unpriced
                                        .map(contract => contract.NrContract)
                                        .join(', '),
                                })}
                            </Alert>
                        )}

                        <Typography variant="h6" sx={{ mt: 2 }}>
                            {t('payment.total')}: {formatCurrency(batchSummary.total)}
                        </Typography>

                        {batchProcessing && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {t('payment.batch.progress', {
                                    done: batchProgress,
                                    total: batchSummary.lines.length,
                                })}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setBatchDialogOpen(false)}
                            disabled={batchProcessing}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleRunBatchPayment}
                            variant="contained"
                            color="primary"
                            data-testid="batch-confirm"
                            disabled={batchProcessing || batchSummary.lines.length === 0}
                        >
                            {batchProcessing ? t('common.processing') : t('common.confirm')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Extension History Dialog */}
                <Dialog
                    open={extensionHistoryOpen}
                    onClose={() => setExtensionHistoryOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>{t('payment.extensionHistory.title')}</DialogTitle>
                    <DialogContent>
                        {extensionHistoryLoading && <Typography>{t('common.loading')}</Typography>}
                        {!extensionHistoryLoading && extensionHistory.length === 0 && (
                            <Typography>{t('payment.extensionHistory.noExtensions')}</Typography>
                        )}
                        {!extensionHistoryLoading && extensionHistory.length > 0 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>
                                                {t('payment.extensionHistory.date')}
                                            </TableCell>
                                            <TableCell align="right">
                                                {t('payment.extensionHistory.amountPaid')}
                                            </TableCell>
                                            <TableCell>
                                                {t('payment.extensionHistory.extendedUntil')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {extensionHistory.map(ext => (
                                            <TableRow key={ext.extensionNumber}>
                                                <TableCell>{ext.extensionNumber}</TableCell>
                                                <TableCell>
                                                    {ext.extensionDate
                                                        ? dayjs(ext.extensionDate).format(
                                                              'DD.MM.YYYY',
                                                          )
                                                        : '-'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {ext.amountRon
                                                        ? `${Math.round(Number(ext.amountRon))} RON`
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {ext.newDueDate
                                                        ? dayjs(ext.newDueDate).format('DD.MM.YYYY')
                                                        : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setExtensionHistoryOpen(false)}>
                            {t('common.close')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default Payment;
